import React, { useState, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
} from '@mui/material';
import { useJobs } from '../hooks/useJobs';
import { getTranscriptionText } from '../utils/helpers';
import { logInfo, logError } from '../utils/logging';
import JobStatusSummary from './jobDashboard/JobStatusSummary';
import FileUpload from './jobDashboard/FileUpload';
import JobsList from './jobDashboard/JobsList';
import JobResultDialog from './jobDashboard/JobResultDialog';
import Notification from './common/Notification';

const JobDashboard = () => {
  // API key id for normal uploads (JWT)
  const [uploadApiKeyId, setUploadApiKeyId] = useState(() => {
    const storedId = localStorage.getItem('jobsApiKeyId');
    return storedId ? Number(storedId) : null;
  });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  
  // Vždy načítat všechny joby (bez filtrování podle API klíče)
  const { jobs, loading, activeJobs, maxConcurrent, totalJobs, createJob, cancelJob, refetch } = useJobs(null, page, pageSize);
  
  const [resultOpen, setResultOpen] = useState(false);
  const [resultJob, setResultJob] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleApiKeyIdChange = useCallback((newApiKeyId) => {
    setUploadApiKeyId(prevId => {
      if (prevId !== newApiKeyId) {
        if (newApiKeyId === null || newApiKeyId === '') {
          localStorage.removeItem('jobsApiKeyId');
        } else {
          localStorage.setItem('jobsApiKeyId', String(newApiKeyId));
        }
        logInfo('Upload API Key ID changed:', { newValue: newApiKeyId });
        return newApiKeyId;
      }
      return prevId;
    });
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleUpload = async (file, apiKeyId, onProgress, wordTimestamps = false, enableDiarization = false) => {
    try {
      const response = await createJob(file, apiKeyId, onProgress, wordTimestamps, enableDiarization);
      logInfo('Job created:', { job: response });
      showSnackbar(
          'Job created successfully! You can monitor its progress below.',
          'success'
      );
      // Reset to first page to see the new job
      setPage(1);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message;
      throw new Error(errorMessage);
    }
  };
  
  const handleCancelJob = async (jobId) => {
      try {
          await cancelJob(jobId);
          showSnackbar('Job cancelled successfully.', 'success');
      } catch (error) {
          showSnackbar(`Failed to cancel job: ${error.response?.data?.detail || error.message}`, 'error');
      }
  };

  const handleViewResult = (job) => {
    if (!job?.result_data?.transcription) {
      showSnackbar('No transcription available.', 'warning');
      return;
    }
    setResultJob(job);
    setResultOpen(true);
  };

  const handleCopyResult = async () => {
    if (!resultJob) return;
    
    const textToCopy = getTranscriptionText(resultJob) || '';
    if (!textToCopy) {
      showSnackbar('Nothing to copy', 'warning');
      return;
    }

    // 1) Modern clipboard API (works in HTTPS / localhost)
    try {
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
        showSnackbar('Transcription copied to clipboard', 'success');
        return;
      }
    } catch (err) {
      // Continue to fallback
      logInfo('Clipboard API failed, trying fallback', { error: err });
    }

    // 2) Fallback (more compatible)
    try {
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;

      // Prevent scrolling to bottom on iOS / keep off-screen
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '1px';
      textArea.style.height = '1px';
      textArea.style.opacity = '0';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, textArea.value.length); // important for mobile

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (!successful) {
        throw new Error('execCommand(copy) returned false');
      }

      showSnackbar('Transcription copied to clipboard', 'success');
    } catch (err) {
      logError(err, { context: 'JobDashboard handleCopyResult fallback' });
      showSnackbar('Failed to copy to clipboard', 'error');
    }
  };

const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Job Dashboard
        </Typography>
        
        <JobStatusSummary jobs={jobs} activeJobs={activeJobs} maxConcurrent={maxConcurrent} />

        <FileUpload 
            apiKeyId={uploadApiKeyId}
            onApiKeyIdChange={handleApiKeyIdChange}
            onUpload={handleUpload}
            activeJobs={activeJobs}
            maxConcurrent={maxConcurrent}
        />

        <JobsList 
            jobs={jobs}
            loading={loading}
            onCancelJob={handleCancelJob}
            onViewResult={handleViewResult}
            onRefresh={refetch} // Manual refresh - polling only runs when there are active jobs
            page={page}
            pageSize={pageSize}
            total={totalJobs}
            onPageChange={(newPage) => setPage(newPage)}
        />
      </Paper>
      
      <JobResultDialog
        open={resultOpen}
        onClose={() => setResultOpen(false)}
        job={resultJob}
        onCopy={handleCopyResult}
      />

      <Notification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </Container>
  );
};

export default JobDashboard;